import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealApiService } from '../services/deal-api.service';
import { DealContactApiService } from '../services/deal-contact-api.service';
import {
  DealResponse,
  DealRequest,
  DealCycle,
  DealContactRoleResponse,
  DealBusinessType,
  DealPriority,
} from '../models/deal.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import {
  CompanyResponse,
  ContactCompanyResponse,
} from '../../empresas/models/empresa.model';
import { ContactoDrawerService } from '../../contactos/services/contacto-drawer.service';
import { EmpresaDrawerService } from '../../empresas/services/empresa-drawer.service';
import { ToastService } from '../../../shared/services/toast.service';
import { CrmUserResponse } from '../../users/models/user.model';

interface PendingDealContact {
  contact: Contact;
  idDealContactRole?: number;
  isPrimary: boolean;
  notes: string;
  associationId?: number; // set when loaded from API in edit mode
  fromLead?: boolean; // true when auto-filled from a lead selection
}

// Resumen agregado de las sub-operaciones (asociaciones de contactos) del guardado
interface SubOpsResult {
  failed: number; // cuántas operaciones fallaron
  forbidden: boolean; // al menos un fallo fue 403 (sin permisos)
}

@Component({
  selector: 'app-deal-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './deal-form.component.html',
})
export class DealFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  submitAttempted = false;
  dealId: number | null = null;
  currentDeal: DealResponse | null = null;

  // Lista de usuarios (Responsables/Responsables)
  users: CrmUserResponse[] = [];

  // Listas para selects (catálogos)
  dealCycles: DealCycle[] = [];
  dealBusinessTypes: DealBusinessType[] = [];
  dealPriorities: DealPriority[] = [];
  loadingCatalogs = false;

  // Deal-Contact associations
  dealContacts: PendingDealContact[] = [];
  associationsToDelete: number[] = [];
  contactRoles: DealContactRoleResponse[] = [];
  loadingContactAssociations = false;

  // Contact typeahead search (for "Contactos Asociados" junction table)
  contactSearchTerm = '';
  contactSearchResults: Contact[] = [];
  showContactDropdown = false;
  searchingContacts = false;
  loadingMoreContacts = false;
  contactSearchPage = 0;
  contactSearchTotalPages = 0;
  selectedContactForAdd: Contact | null = null;
  newContactRole: number | undefined = undefined;
  newContactNotes = '';

  private readonly contactSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  // Company typeahead
  companySearchTerm = '';
  companySearchResults: CompanyResponse[] = [];
  showCompanyDropdown = false;
  searchingCompanies = false;
  loadingMoreCompanies = false;
  companySearchPage = 0;
  companySearchTotalPages = 0;
  selectedCompany: CompanyResponse | null = null;
  companyAutoFilled = false;

  minDatetimeLocal = '';

  // Modal for multiple companies
  showCompanyChoiceModal = false;
  contactCompaniesForModal: ContactCompanyResponse[] = [];

  // Inc 11 - modal de negocio duplicado
  showDuplicateModal = false;
  duplicateExistingId: number | null = null;
  duplicateExistingName = '';
  private pendingDealRequest: DealRequest | null = null;
  private pendingBusinessId = 0;

  private readonly companySearch$ = new Subject<string>();

  // Drawer mode support
  @Input() drawerMode = false;
  @Input() set prefilledContactId(id: number | undefined) {
    this._prefilledContactId = id;
  }
  @Input() set prefilledCompanyId(id: number | undefined) {
    this._prefilledCompanyId = id;
  }
  @Input() set prefilledCompanyName(name: string | undefined) {
    this._prefilledCompanyName = name;
  }
  @Input() set prefilledContactName(name: string | undefined) {
    this._prefilledContactName = name;
  }
  /**
   * Inc 8 — lead de origen cuando el negocio se crea desde un lead (kanban de
   * leads, trigger CREATE_DEAL). Sin esto el negocio se guardaba con
   * id_lead = NULL y el guard de borrado del backend (que cuenta negocios
   * abiertos del lead) contaba 0 y dejaba eliminar el lead.
   */
  @Input() set prefilledLeadId(id: number | undefined) {
    this._prefilledLeadId = id;
  }
  @Output() saved = new EventEmitter<DealResponse>();
  @Output() cancelled = new EventEmitter<void>();

  private _prefilledContactId?: number;
  private _prefilledCompanyId?: number;
  private _prefilledCompanyName?: string;
  private _prefilledContactName?: string;
  private _prefilledLeadId?: number;

  constructor(
    private fb: FormBuilder,
    private dealApi: DealApiService,
    private dealContactApi: DealContactApiService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private contactCompanyApi: ContactCompanyApiService,
    private authService: AuthService,
    private datetimeService: DatetimeService,
    private contactoDrawerService: ContactoDrawerService,
    private empresaDrawerService: EmpresaDrawerService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Cargar catálogos
    this.cargarCatalogos();
    this.cargarRoles();

    // Cargar usuarios
    this.cargarUsuarios();

    // Set currently date to min date posible select
    this.updateMinDate();

    // Setup contact search typeahead (for "Contactos Asociados" junction table)
    this.contactSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.contactSearchPage = 0;
          this.contactSearchResults = [];
          this.contactSearchTotalPages = 0;

          if (term.length === 0) {
            this.showContactDropdown = false;
            return of(null);
          }

          this.searchingContacts = true;
          const user = this.authService.getCurrentUser();
          const businessId = user?.businessId || 1;
          return this.contactoApi
            .searchContacts({
              businessId,
              searchTerm: term,
              page: 0,
              size: 10,
            })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingContacts = false;
        if (response) {
          this.contactSearchResults = response.content;
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });

    this.companySearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.length < 2) {
            this.showCompanyDropdown = false;
            return of(null);
          }
          this.companySearchPage = 0;
          this.companySearchResults = [];
          this.companySearchTotalPages = 0;
          this.searchingCompanies = true;
          const user = this.authService.getCurrentUser();
          const businessId = user?.businessId || 1;
          return this.empresaApi
            .searchCompanies({
              businessId,
              searchTerm: term,
              page: 0,
              size: 10,
            })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingCompanies = false;
        if (response) {
          this.companySearchResults = response.content;
          this.companySearchTotalPages = response.totalPages;
          this.showCompanyDropdown = this.companySearchResults.length > 0;
        }
      });

    // Escuchar contacto guardado desde el drawer
    this.contactoDrawerService.contactoSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((contact) => {
        if (contact?.id) {
          const alreadyAdded = this.dealContacts.some(
            (dc) => dc.contact.id === contact.id,
          );
          if (!alreadyAdded) {
            const isPrimary = this.dealContacts.length === 0;
            this.dealContacts = [
              ...this.dealContacts,
              {
                contact,
                idDealContactRole: undefined,
                isPrimary,
                notes: '',
                associationId: undefined,
              },
            ];
          }
        }
      });

    // Escuchar empresa guardada desde el drawer
    this.empresaDrawerService.empresaSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((empresa) => {
        if (empresa?.id) {
          this.selectCompanyFromDropdown(empresa);
          this.flashCompanyAutoFill();
        }
      });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.dealId = +id;
      this.cargarDeal(this.dealId);
      this.cargarContactosAsociados(this.dealId);
    }
  }

  private updateMinDate(): void {
    this.minDatetimeLocal = this.toLocalInputFromDate(new Date());
  }

  private toLocalInputFromDate(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarUsuarios(): void {
    const user = this.authService.getCurrentUser();

    const businessId = user?.businessId || 1;
    this.dealApi.getUsersByBusiness(businessId).subscribe((users) => {
      this.users = users;
    });

    if (!this.isEditing && user?.id) {
      this.form.patchValue({ userPropietyId: user.id });
    }
  }

  private cargarCatalogos(): void {
    this.loadingCatalogs = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    Promise.all([
      this.dealApi.getDealCycles().toPromise(),
      this.dealApi.getDealBusinessTypes(businessId).toPromise(),
      this.dealApi.getDealPriorities(businessId).toPromise(),
    ])
      .then(([cycles, businessTypes, priorities]) => {
        this.dealCycles = cycles || [];
        this.dealBusinessTypes = businessTypes || [];
        this.dealPriorities = priorities || [];
        this.loadingCatalogs = false;
        if (this.drawerMode && !this.isEditing) {
          this.applyPrefill();
        }
      })
      .catch(() => {
        this.loadingCatalogs = false;
        this.error = 'Error al cargar catálogos';
      });
  }

  private cargarRoles(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealContactApi.getActiveRoles(businessId).subscribe({
      next: (roles) => {
        this.contactRoles = roles;
      },
      error: () => {
        this.contactRoles = [];
      },
    });
  }

  cargarContactosAsociados(dealId: number): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.loadingContactAssociations = true;

    this.dealContactApi.getByDeal(dealId, businessId).subscribe({
      next: (associations) => {
        if (associations.length === 0) {
          this.loadingContactAssociations = false;
          return;
        }

        const contactRequests = associations.map((assoc) =>
          this.contactoApi
            .getContactById(assoc.idContact)
            .pipe(catchError(() => of(null))),
        );

        forkJoin(contactRequests).subscribe({
          next: (contactResults) => {
            this.dealContacts = associations
              .map((assoc, index) => {
                const c = contactResults[index];
                if (!c) return null;
                return {
                  contact: c,
                  idDealContactRole: assoc.idDealContactRole,
                  isPrimary: assoc.isPrimary,
                  notes: assoc.notes || '',
                  associationId: assoc.id,
                } as PendingDealContact;
              })
              .filter((item): item is PendingDealContact => item !== null);
            this.loadingContactAssociations = false;
          },
          error: () => {
            this.loadingContactAssociations = false;
          },
        });
      },
      error: () => {
        this.loadingContactAssociations = false;
      },
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(1000)],
      amount: [null, [Validators.min(0.01)]],
      prefix: ['PEN', [Validators.required]],
      idDealCycle: [null, Validators.required],
      idCompany: [null],
      // Inc 8 — vínculo con el lead de origen. No se edita en la UI, pero viaja
      // en el POST y se re-envía en el PUT para no perder la trazabilidad.
      idLead: [null],
      idDealBusinessType: [null],
      userPropietyId: [null, Validators.required],
      idDealPriority: [null],
      datetimeEstimatedClose: [null, [this.futureDateValidator()]],
    });
  }

  /**
   * Fecha estimada de cierre tal como vino del backend al abrir el negocio. `null` al crear.
   */
  private fechaCierreOriginal: string | null = null;

  /**
   * La fecha estimada de cierre no puede ser pasada... salvo la que YA estaba guardada.
   *
   * Un negocio se retrasa: su cierre estimado queda en el pasado constantemente. Validándolo sin
   * excepción, el formulario nacía inválido y el botón de guardar —`[disabled]="saving ||
   * form.invalid || …"`— salía deshabilitado, así que no se podía ni mover de etapa ni cambiar
   * el importe hasta reescribir la fecha. Solo se rechaza una fecha pasada NUEVA.
   */
  private futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      if (this.fechaCierreOriginal !== null && control.value === this.fechaCierreOriginal) {
        return null;
      }
      const val = new Date(control.value);
      const now = new Date();
      val.setSeconds(0, 0);
      now.setSeconds(0, 0);
      return val < now ? { pastDate: true } : null;
    };
  }

  activesDealBusinessTypes() {
    return this.dealBusinessTypes.filter((type) => type.isActive);
  }

  cargarDeal(id: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi.getDealById(id, businessId).subscribe({
      next: (deal: DealResponse) => {
        this.currentDeal = deal;
        this.form.patchValue({
          userPropietyId: deal.userPropietyId,
          name: deal.name,
          description: deal.description,
          amount: deal.amount,
          prefix: deal.prefix,
          idDealCycle: deal.idDealCycle,
          idCompany: deal.idCompany,
          // Inc 8 — sin esto, editar un negocio creado desde un lead lo enviaba
          // sin idLead en el PUT y borraba el vínculo (el lead volvía a ser
          // borrable aunque tuviera el negocio abierto).
          idLead: deal.idLead ?? null,
          idDealBusinessType: deal.idDealBusinessType,
          idDealPriority: deal.idDealPriority,
          datetimeEstimatedClose: deal.datetimeEstimatedClose
            ? this.datetimeService.utcToLocalInput(deal.datetimeEstimatedClose)
            : null,
        });
        this.fechaCierreOriginal = deal.datetimeEstimatedClose
          ? this.datetimeService.utcToLocalInput(deal.datetimeEstimatedClose)
          : null;

        if (deal.idCompany) {
          if (deal.companyName) {
            this.companySearchTerm = deal.companyName;
            this.selectedCompany = {
              id: deal.idCompany,
              commercialName: deal.companyName,
              businessId,
            } as CompanyResponse;
          } else {
            this.empresaApi
              .getCompanyById(deal.idCompany)
              .pipe(
                catchError(() => of(null)),
                takeUntil(this.destroy$),
              )
              .subscribe((company) => {
                if (company) {
                  this.companySearchTerm = company.commercialName;
                  this.selectedCompany = company;
                } else {
                  this.companySearchTerm = `Empresa #${deal.idCompany}`;
                }
              });
          }
        }
        if (this.isClosed) {
          this.form.disable();
        }

        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el negocio';
        this.loading = false;
      },
    });
  }

  // Associated contacts typeahead (junction table)
  onContactSearchInput(term: string): void {
    this.contactSearchTerm = term;
    this.selectedContactForAdd = null;
    this.contactSearch$.next(term);
  }

  onContactSearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactSearchPage = 0;
    this.contactSearchResults = [];
    this.contactSearchTotalPages = 0;
    this.searchingContacts = true;
    this.showContactDropdown = false;

    this.contactoApi
      .searchContacts({
        businessId,
        searchTerm: this.contactSearchTerm.trim(),
        page: 0,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingContacts = false;
        if (response) {
          this.contactSearchResults = response.content;
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  onDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (!nearBottom || this.loadingMoreContacts) return;
    if (this.contactSearchPage + 1 >= this.contactSearchTotalPages) return;

    this.loadingMoreContacts = true;
    this.contactSearchPage++;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactoApi
      .searchContacts({
        businessId,
        searchTerm: this.contactSearchTerm.trim(),
        page: this.contactSearchPage,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.loadingMoreContacts = false;
        if (response) {
          this.contactSearchResults = [
            ...this.contactSearchResults,
            ...response.content,
          ];
          this.contactSearchTotalPages = response.totalPages;
        }
      });
  }

  selectContactFromDropdown(contact: Contact): void {
    this.selectedContactForAdd = contact;
    this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
    this.showContactDropdown = false;
    this.contactSearchResults = [];
  }

  closeContactDropdown(): void {
    setTimeout(() => {
      this.showContactDropdown = false;
    }, 200);
  }

  private flashCompanyAutoFill(): void {
    this.companyAutoFilled = true;
    setTimeout(() => {
      this.companyAutoFilled = false;
    }, 2000);
  }

  onCompanySearchInput(term: string): void {
    this.companySearchTerm = term;
    if (!term) {
      this.form.patchValue({ idCompany: null });
      this.selectedCompany = null;
    }
    this.form.markAsDirty();
    this.companySearch$.next(term);
  }

  onCompanySearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.companySearchPage = 0;
    this.companySearchResults = [];
    this.companySearchTotalPages = 0;
    this.searchingCompanies = true;
    this.showCompanyDropdown = false;

    this.empresaApi
      .searchCompanies({
        businessId,
        searchTerm: this.companySearchTerm.trim(),
        page: 0,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingCompanies = false;
        if (response) {
          this.companySearchResults = response.content;
          this.companySearchTotalPages = response.totalPages;
          this.showCompanyDropdown = this.companySearchResults.length > 0;
        }
      });
  }

  onCompanyDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (!nearBottom || this.loadingMoreCompanies) return;
    if (this.companySearchPage + 1 >= this.companySearchTotalPages) return;

    this.loadingMoreCompanies = true;
    this.companySearchPage++;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.empresaApi
      .searchCompanies({
        businessId,
        searchTerm: this.companySearchTerm.trim(),
        page: this.companySearchPage,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.loadingMoreCompanies = false;
        if (response) {
          this.companySearchResults = [
            ...this.companySearchResults,
            ...response.content,
          ];
          this.companySearchTotalPages = response.totalPages;
        }
      });
  }

  selectCompanyFromDropdown(company: CompanyResponse): void {
    this.selectedCompany = company;
    this.companySearchTerm = company.commercialName;
    this.form.patchValue({ idCompany: company.id });
    this.showCompanyDropdown = false;
    this.companySearchResults = [];
    this.form.markAsDirty();
  }

  clearCompany(): void {
    this.selectedCompany = null;
    this.companySearchTerm = '';
    this.form.patchValue({ idCompany: null });
    this.form.markAsDirty();
  }

  closeCompanyDropdown(): void {
    setTimeout(() => {
      this.showCompanyDropdown = false;
    }, 200);
  }

  abrirNuevoContacto(): void {
    this.contactoDrawerService.open();
  }

  abrirNuevaEmpresa(): void {
    this.empresaDrawerService.open();
  }

  selectCompanyInModal(assoc: ContactCompanyResponse): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.form.patchValue({ idCompany: assoc.idCompany });
    this.companySearchTerm = assoc.company?.commercialName || '';
    this.selectedCompany = {
      id: assoc.idCompany,
      commercialName: assoc.company?.commercialName || '',
      businessId,
    } as CompanyResponse;
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
    this.flashCompanyAutoFill();
  }

  skipCompanyInModal(): void {
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
  }

  agregarContacto(): void {
    if (!this.selectedContactForAdd) return;

    // Evitar duplicados
    const alreadyAdded = this.dealContacts.some(
      (dc) => dc.contact.id === this.selectedContactForAdd!.id,
    );
    if (alreadyAdded) {
      return;
    }

    const contact = this.selectedContactForAdd;

    this.dealContacts = [
      ...this.dealContacts,
      {
        contact,
        idDealContactRole: this.newContactRole,
        isPrimary: false,
        notes: this.newContactNotes,
        associationId: undefined,
      },
    ];

    // Reset add form
    this.selectedContactForAdd = null;
    this.contactSearchTerm = '';
    this.newContactRole = undefined;
    this.newContactNotes = '';
    this.contactSearchResults = [];
    this.showContactDropdown = false;
    this.form.markAsDirty();

    // Si no hay empresa seleccionada, sugerir empresa del contacto
    if (!this.form.get('idCompany')?.value && contact.id) {
      const businessId = this.authService.getCurrentUser()?.businessId || 1;
      this.contactCompanyApi
        .getByContact(contact.id, businessId)
        .pipe(catchError(() => of([] as ContactCompanyResponse[])))
        .subscribe((assocs) => {
          if (assocs.length === 1) {
            const assoc = assocs[0];
            this.form.patchValue({ idCompany: assoc.idCompany });
            this.companySearchTerm = assoc.company?.commercialName || '';
            this.selectedCompany = {
              id: assoc.idCompany,
              commercialName: assoc.company?.commercialName || '',
              businessId,
            } as CompanyResponse;
            this.flashCompanyAutoFill();
          } else if (assocs.length > 1) {
            this.contactCompaniesForModal = assocs;
            this.showCompanyChoiceModal = true;
          }
        });
    }
  }

  quitarContacto(index: number): void {
    const dc = this.dealContacts[index];
    if (dc.associationId) {
      this.associationsToDelete.push(dc.associationId);
      this.form.markAsDirty();
    }
    this.dealContacts = this.dealContacts.filter((_, i) => i !== index);
  }

  togglePrimaryContact(index: number): void {
    const wasPrimary = this.dealContacts[index].isPrimary;
    this.dealContacts = this.dealContacts.map((dc, i) => ({
      ...dc,
      isPrimary: i === index ? !wasPrimary : false,
    }));
    this.form.markAsDirty();
  }

  getRoleName(idRole?: number): string {
    if (!idRole) return '';
    const role = this.contactRoles.find((r) => r.id === idRole);
    return role?.name || '';
  }

  getContactInitials(contact: Contact): string {
    const first = contact.fullname?.charAt(0) || '';
    const last = contact.lastname?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  getContactAvatarColor(contact: Contact): string {
    const name = (contact.fullname || '') + (contact.lastname || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-indigo-500',
      'bg-red-500',
    ];
    return colors[Math.abs(hash) % colors.length];
  }

  private async guardarAsociacionesDealContacts(
    dealId: number,
    businessId: number,
    isEdit: boolean,
  ): Promise<SubOpsResult> {
    const deletePromises: Promise<unknown>[] = this.associationsToDelete.map(
      (id) =>
        this.dealContactApi
          .deleteAssociation(id, businessId)
          .toPromise()
          .then(() => null)
          .catch((err) => err),
    );

    const existingIds = new Set(
      this.dealContacts
        .filter((dc) => dc.associationId !== undefined)
        .map((dc) => dc.associationId!),
    );

    // New associations (no associationId)
    const createPromises: Promise<unknown>[] = this.dealContacts
      .filter((dc) => dc.associationId === undefined)
      .map((dc) =>
        this.dealContactApi
          .createAssociation({
            idDeal: dealId,
            idContact: dc.contact.id!,
            businessId,
            idDealContactRole: dc.idDealContactRole,
            isPrimary: dc.isPrimary,
            notes: dc.notes || undefined,
          })
          .toPromise()
          .then(() => null)
          .catch((err) => err),
      );

    // Existing associations that need update (already have associationId)
    const updatePromises: Promise<unknown>[] = this.dealContacts
      .filter((dc) => dc.associationId !== undefined)
      .map((dc) =>
        this.dealContactApi
          .updateAssociation(
            dc.associationId!,
            {
              idDeal: dealId,
              idContact: dc.contact.id!,
              businessId,
              idDealContactRole: dc.idDealContactRole,
              isPrimary: dc.isPrimary,
              notes: dc.notes || undefined,
            },
            businessId,
          )
          .toPromise()
          .then(() => null)
          .catch((err) => err),
      );

    const results = await Promise.all([
      ...deletePromises,
      ...createPromises,
      ...updatePromises,
    ]);

    // Acumular fallos sin abortar el resto (cada promesa ya tiene su catch)
    const errors = results.filter(
      (r): r is { status?: number } => r !== null && r !== undefined,
    );
    if (errors.length > 0) {
      console.warn(
        `[DealForm] ${errors.length} operación(es) de contactos asociados fallaron al guardar:`,
        errors,
      );
    }
    return {
      failed: errors.length,
      forbidden: errors.some((e) => e?.status === 403),
    };
  }

  /**
   * Feedback consolidado del guardado: el negocio base ya se guardó; si las
   * asociaciones de contactos fallaron, un solo toast de advertencia lo indica.
   */
  private notificarResultadoGuardado(
    accion: 'creó' | 'actualizó',
    contactsResult: SubOpsResult,
  ): void {
    if (contactsResult.failed === 0) {
      if (accion === 'creó') {
        this.toast.created('Negocio');
      } else {
        this.toast.updated('Negocio');
      }
      return;
    }
    let message = `El negocio se ${accion}, pero no se pudieron guardar los contactos asociados.`;
    if (contactsResult.forbidden) {
      message += ' No tienes permisos para modificarlos.';
    }
    this.toast.warning({ title: 'Guardado parcial', message, duration: 8000 });
  }

  get isClosed(): boolean {
    return !!this.currentDeal?.datetimeClosed;
  }

  get pageTitle(): string {
    if (this.isEditing) {
      return this.isClosed ? 'Ver Negocio (Cerrado)' : 'Editar Negocio';
    }
    return 'Nuevo Negocio';
  }

  get selectedCurrency(): string {
    return this.form.get('prefix')?.value || 'PEN';
  }

  get contactsInvalid(): boolean {
    return this.dealContacts.length === 0;
  }

  guardar(): void {
    this.submitAttempted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isClosed) {
      this.error =
        'No se puede editar un negocio cerrado. Debe reabrirlo primero.';
      return;
    }

    if (this.contactsInvalid) {
      this.error = 'Debe asociar al menos un contacto al negocio.';
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const dealRequest: DealRequest = {
      ...this.form.value,
      businessId,

      name: this.form.get('name')?.value.trim(),
    };

    if (!dealRequest.amount) dealRequest.amount = undefined;
    if (!dealRequest.idCompany) dealRequest.idCompany = undefined;
    if (!dealRequest.idLead) dealRequest.idLead = undefined;
    if (!dealRequest.idDealBusinessType)
      dealRequest.idDealBusinessType = undefined;
    if (!dealRequest.idDealPriority) dealRequest.idDealPriority = undefined;
    if (dealRequest.datetimeEstimatedClose) {
      dealRequest.datetimeEstimatedClose = this.datetimeService.localToUtcIso(
        dealRequest.datetimeEstimatedClose,
      );
    } else {
      dealRequest.datetimeEstimatedClose = undefined;
    }

    if (this.form.pristine) {
      this.toast.info({
        title: 'Negocio',
        message: 'No se realizaron nuevos cambios en el negocio',
      });
      this.router.navigate(['/deals']);
      return;
    }

    // Inc 11 — antes de guardar, avisar si ya existe un negocio duplicado
    // (mismo nombre + empresa + contacto principal + pipeline).
    this.dealApi
      .checkDuplicate({
        businessId,
        name: dealRequest.name,
        idCompany: dealRequest.idCompany,
        idContact: this.getPrimaryContactId(),
        idDealCycle: dealRequest.idDealCycle,
      })
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        // No duplicado (o el check falló): continuar con el guardado normal.
        if (!res || !res.duplicate || res.existingDealId === this.dealId) {
          this.doSave(dealRequest, businessId, false);
          return;
        }
        // Duplicado: pausar el guardado y mostrar el modal de decisión.
        this.saving = false;
        this.openDuplicateModal(
          dealRequest,
          businessId,
          res.existingDealId,
          res.existingDealName || dealRequest.name,
        );
      });
  }

  // Inc 11 — abre el modal de duplicado guardando el estado necesario para
  // "Abrir el existente" y "Crear de todos modos".
  private openDuplicateModal(
    request: DealRequest,
    businessId: number,
    existingId: number | null,
    existingName: string,
  ): void {
    this.pendingDealRequest = request;
    this.pendingBusinessId = businessId;
    this.duplicateExistingId = existingId;
    this.duplicateExistingName = existingName;
    this.showDuplicateModal = true;
  }

  /**
   * Inc 11 — obtiene el id del contacto principal (o el primero si no se marcó
   * ninguno como principal) para la verificación de duplicados.
   */
  private getPrimaryContactId(): number | undefined {
    const primary = this.dealContacts.find((dc) => dc.isPrimary);
    return primary?.contact.id ?? this.dealContacts[0]?.contact.id;
  }

  /**
   * Inc 11 — ejecuta el POST/PUT real. `allowDuplicate` viaja al backend para
   * permitir crear/renombrar aunque exista un duplicado.
   */
  private doSave(
    dealRequest: DealRequest,
    businessId: number,
    allowDuplicate: boolean,
  ): void {
    this.saving = true;
    this.error = '';
    const request: DealRequest = { ...dealRequest, allowDuplicate };

    if (this.isEditing && this.dealId) {
      this.dealApi.updateDeal(this.dealId, request).subscribe({
        next: async (updatedDeal) => {
          const contactsResult = await this.guardarAsociacionesDealContacts(
            updatedDeal.id,
            businessId,
            true,
          );
          this.saving = false;
          this.notificarResultadoGuardado('actualizó', contactsResult);
          if (this.drawerMode) {
            this.saved.emit(updatedDeal);
          } else {
            this.router.navigate(['/deals']);
          }
        },
        error: (err) => this.handleSaveError(err, request, businessId),
      });
    } else {
      this.dealApi.createDeal(request).subscribe({
        next: async (createdDeal) => {
          const contactsResult = await this.guardarAsociacionesDealContacts(
            createdDeal.id,
            businessId,
            false,
          );
          this.saving = false;
          this.notificarResultadoGuardado('creó', contactsResult);
          if (this.drawerMode) {
            this.saved.emit(createdDeal);
          } else {
            this.router.navigate(['/deals']);
          }
        },
        error: (err) => this.handleSaveError(err, request, businessId),
      });
    }
  }

  /**
   * Inc 11 — manejo de errores del guardado. Red de seguridad: si el backend
   * responde 409 por duplicado, mostramos el mismo modal de decisión.
   */
  private handleSaveError(
    err: {
      status?: number;
      error?: {
        message?: string;
        existingDealId?: number;
        existingDealName?: string;
      };
    },
    request: DealRequest,
    businessId: number,
  ): void {
    this.saving = false;

    if (err?.status === 409) {
      // Preferir el id del body del 409 si el backend lo incluye.
      const bodyId = err.error?.existingDealId ?? null;
      const bodyName = err.error?.existingDealName ?? request.name;
      if (bodyId) {
        this.openDuplicateModal(request, businessId, bodyId, bodyName);
        return;
      }
      // El 409 no trae id → recuperarlo con check-duplicate (criterio del backend:
      // nombre + empresa + pipeline) para poder ofrecer "Abrir el existente".
      this.dealApi
        .checkDuplicate({
          businessId,
          name: request.name,
          idCompany: request.idCompany,
          idDealCycle: request.idDealCycle,
        })
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          this.openDuplicateModal(
            request,
            businessId,
            res?.existingDealId ?? null,
            res?.existingDealName ?? request.name,
          );
        });
      return;
    }

    this.error = err.error?.message || 'Error al guardar el negocio';
    if (
      err.error?.message ===
      `A deal with name '${request.name}' already exists in this business`
    ) {
      this.error = `Ya existe un negocio con el nombre '${request.name}' en esta empresa`;
    }
    this.toast.error({ title: 'Error', message: this.error });
  }

  // Inc 11 — acciones del modal de duplicado
  confirmarCrearDuplicado(): void {
    if (!this.pendingDealRequest) return;
    const req = this.pendingDealRequest;
    const businessId = this.pendingBusinessId;
    this.cerrarDuplicadoModal();
    this.doSave(req, businessId, true);
  }

  abrirNegocioExistente(): void {
    const id = this.duplicateExistingId;

    this.cerrarDuplicadoModal();

    if (!id) return;

    if (this.drawerMode) {
      this.cancelled.emit();
    }

    // El detalle de un negocio es un modal sobre el kanban: /deals?openDeal=<id>
    // lo carga por id y lo abre aunque no esté en la página visible (el
    // mecanismo anterior, `state.openDealId`, solo funcionaba si el negocio
    // venía en la primera página de resultados).
    this.router.navigate(['/deals'], { queryParams: { openDeal: id } });
  }

  cerrarDuplicadoModal(): void {
    this.showDuplicateModal = false;
    this.pendingDealRequest = null;
    this.pendingBusinessId = 0;
    this.duplicateExistingId = null;
    this.duplicateExistingName = '';
    this.saving = false;
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/deals']);
    }
  }

  // Public methods for drawer usage
  reset(): void {
    this.isEditing = false;
    this.dealId = null;
    // El drawer se reutiliza para crear: la fecha del negocio anterior no debe quedar eximida.
    this.fechaCierreOriginal = null;
    this.currentDeal = null;
    this.dealContacts = [];
    this.associationsToDelete = [];
    this.companySearchTerm = '';
    this.selectedCompany = null;
    this.contactSearchTerm = '';
    this.selectedContactForAdd = null;
    this.newContactRole = undefined;
    this.newContactNotes = '';
    this.contactSearchResults = [];
    this.companySearchResults = [];
    this.showContactDropdown = false;
    this.showCompanyDropdown = false;
    this.submitAttempted = false;
    this.error = '';
    this.saving = false;
    this.companyAutoFilled = false;
    this.showDuplicateModal = false;
    this.duplicateExistingId = null;
    this.duplicateExistingName = '';
    this.pendingDealRequest = null;
    this.pendingBusinessId = 0;
    this.initForm();
  }

  loadDeal(id: number): void {
    this.isEditing = true;
    this.dealId = id;
    this.cargarDeal(id);
    this.cargarContactosAsociados(id);
  }

  private applyPrefill(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Inc 8 — el lead de origen llega desde el kanban de leads (CREATE_DEAL).
    if (this._prefilledLeadId) {
      this.form.patchValue({ idLead: this._prefilledLeadId });
    }

    if (this._prefilledCompanyId) {
      this.form.patchValue({ idCompany: this._prefilledCompanyId });
      this.companySearchTerm = this._prefilledCompanyName || '';
      this.selectedCompany = {
        id: this._prefilledCompanyId,
        commercialName: this._prefilledCompanyName || '',
        businessId,
      } as CompanyResponse;
    }

    if (this._prefilledContactId) {
      this.contactoApi
        .getContactById(this._prefilledContactId)
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((contact) => {
          if (contact) {
            const alreadyAdded = this.dealContacts.some(
              (dc) => dc.contact.id === contact.id,
            );
            if (!alreadyAdded) {
              this.dealContacts = [
                ...this.dealContacts,
                {
                  contact,
                  idDealContactRole: undefined,
                  isPrimary: true,
                  notes: '',
                  associationId: undefined,
                },
              ];
            }
          }
        });
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);

    return !!(
      control &&
      control.invalid &&
      (control.touched || this.isEditing)
    );
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Este campo es requerido';

    if (control.errors['maxlength'])
      return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;

    if (control.errors['min'])
      return `El valor mínimo es ${control.errors['min'].min}`;

    if (control.errors['pastDate']) {
      return `Debe seleccionar una fecha mayor al de hoy`;
    }

    return 'Campo inválido';
  }

  formatCurrency(amount?: number): string {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }
}
